import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./NotificationDrawer.module.scss";
import NotificationCard from "./NotificationCard";
import CustomButton from "../controls/button/Button";
import NotificationHeader from "./NotificationHeader";
import { getNotifications } from "../../store/actions/general.actions";
import { notificationsList } from "../../store/selectors/general.selector";
import Skeleton from "react-loading-skeleton";
import { loading } from "../../store/selectors/general.selector";

const NotificationsDrawer = ({
  setIsModalOpen,
  setNotification,
  modalType,
}) => {
  const dispatch = useDispatch();
  const PUBLIC_URL = process.env.PUBLIC_URL;
  const notifications = useSelector(notificationsList);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const isLoading = useSelector(loading);

  const transformLinks = (text, links) => {
    if (!text || text.trim() === "" || !text.includes("<@link:")) {
      return text;
    }

    const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
    return text.replace(regex, (match, type, id) => {
      let entityName;
      if (links && links?.length > 0) {
        entityName = links
          .filter((e) => e.linkId === id)
          .map((e) => e.linkName)
          .join("");
      }
      return entityName || type;
    });
  };

  const filterNotifications = (query, unreadOnly, notificationList) => {
    if (!Array.isArray(notificationList)) {
      setFilteredNotifications([]);
      return;
    }

    let filtered = [...notificationList];

    // Filter by unread
    if (unreadOnly) {
      filtered = filtered.filter((n) => !n.isRead);
    }

    // Filter by search query
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter((n) => {
        const transformedTitle = transformLinks(n.title, n.links).toLowerCase();
        const transformedNotes = transformLinks(n.notes, n.links).toLowerCase();
        return (
          transformedTitle.includes(lowerQuery) ||
          transformedNotes.includes(lowerQuery)
        );
      });
    }

    setFilteredNotifications(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    filterNotifications(query, showUnreadOnly, notifications);
  };

  const handleFilterUnread = () => {
    const newUnreadState = !showUnreadOnly;
    setShowUnreadOnly(newUnreadState);
    filterNotifications(searchQuery, newUnreadState, notifications);
  };

  const handleViewAllClick = () => {
    window.open(`${PUBLIC_URL}/notifications/activity`, "_blank");
  };

  useEffect(() => {
    if (Array.isArray(notifications)) {
      filterNotifications(searchQuery, showUnreadOnly, notifications);
    }
  }, [notifications]);

  useEffect(() => {
    dispatch(getNotifications());
  }, []);

  return (
    <>
      <NotificationHeader
        handleSearch={handleSearch}
        handleFilterUnread={handleFilterUnread}
        isFilterActive={showUnreadOnly}
        headerBg=""
        loading={isLoading}
      />
      <div
        className={`${styles.notifyMainWrap} d-flex flex-column justify-content-between`}
      >
        <div className={styles.notifyBody}>
          {isLoading ? (
            <>
              {[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1].map((i, index) => (
                <div key={index} style={{ marginBottom: "4px" }}>
                  <Skeleton
                    height={60}
                    baseColor="#e0e0e0"
                    highlightColor="#ECE7FD"
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              {typeof filteredNotifications !== "string" &&
              filteredNotifications &&
              filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification, index) => (
                  <NotificationCard
                    key={notification.id || index}
                    item={notification}
                    setIsModalOpen={setIsModalOpen}
                    setNotification={setNotification}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  <p className="text-sm-400 grey36Color text-center">
                    {searchQuery || showUnreadOnly
                      ? "No notifications found"
                      : "No notifications yet"}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <div className={styles.notifyFooter}>
          <CustomButton
            label="View All"
            btnClass={styles.notifyBtn}
            clickHandler={handleViewAllClick}
          />
        </div>
      </div>
    </>
  );
};

export default NotificationsDrawer;
